import styles from 'styles/pages/common/Login.module.scss';
import LabelInput from 'components/common/forms/LabelInput';
import { Link, useNavigate } from 'react-router-dom';
import { ROUTE_PATH } from 'constants/routePath';
import { useState } from 'react';
import SingleBtn from 'components/common/elements/SingleBtn';
import LoginLayout from 'components/common/layout/LoginLayout';
import { UserRoleType, TLoginParams } from 'types/apis/auth';
import { FormProvider, useForm } from 'react-hook-form';
import FormRadioBtn from 'components/common/forms/FormRadioBtn';
import { LoginFormDefaultValues } from 'constants/auth';
import { useMutation } from 'react-query';
import { userInfoAtom } from 'store/auth';
import { useSetAtom } from 'jotai';
import { axiosInstance, isTCustomAxiosError } from 'apis/config/axios';
import useAuth from 'hooks/useAuth';
import { postAllUserLogin } from 'apis/auth';
import { LoginFormField } from 'types/form/auth';
import useModal from 'hooks/useModal';

const Login = () => {
  const { setIsLogin } = useAuth();
  const { openModal } = useModal();
  const navigate = useNavigate();

  const [loginErrorMsg, setLoginErrorMsg] = useState<string | null>(null);

  const setUserInfo = useSetAtom(userInfoAtom);

  const title = '안녕하세요,\n서비스 이용을 위해 로그인을 해주세요.';

  const methods = useForm<TLoginParams>({
    defaultValues: LoginFormDefaultValues,
  });
  const {
    formState: { errors: formErrors },
    handleSubmit,
  } = methods;
  const firstFormErrorKey = Object.keys(formErrors)[0] as LoginFormField;

  const { mutate: postAllUserLoginMutation } = useMutation(
    'postAllUserLogin',
    postAllUserLogin,
    {
      onSuccess: ({ data }) => {
        // 최초 로그인 사용자는 곧바로 비밀번호 재설정 페이지로 이동하므로,
        // 분기 전에 토큰을 저장해 다음 API 요청에도 인증 헤더가 포함되게 한다.
        sessionStorage.setItem('accessToken', data.accessToken);
        axiosInstance.defaults.headers.Authorization = `Bearer ${data.accessToken}`;

        setUserInfo({
          userId: data.userId,
          userName: data.userName,
          userRole: data.userRole,
          accessToken: data.accessToken,
          isAgreeTos: data.isAgreeTos,
          isFirstSetPw: data.isFirstSetPw,
          pwChangeDateOver: data.pwChangeDateOver,
        });
        setIsLogin({ isLogin: true });
        // 첫 로그인 시 또는 비밀번호 수정일이 180일 이상 지나면 비밀번호 변경 페이지로 이동
        if (data.pwChangeDateOver > 180) {
          // 비번 180일 초과 안내 모달
          openModal({
            title: '비밀번호 변경',
            text: '비밀번호 변경일이 180일이 지났습니다. 비밀번호를 변경해 주세요.',
            onSubmit: () => navigate(ROUTE_PATH.common.RESET_PASSWORD),
            onClickOutside: () => navigate(ROUTE_PATH.common.RESET_PASSWORD),
          });
        } else if (!data.isFirstSetPw) {
          navigate(ROUTE_PATH.common.RESET_PASSWORD);
        } else {
          // 약관 동의 여부 확인
          if (!data.isAgreeTos) {
            navigate(ROUTE_PATH.common.TERMS);
          } else {
            if (!data.isFirstSetPw) {
              navigate(ROUTE_PATH.common.MYPAGE);
            } else {
              navigate(ROUTE_PATH.common.HOME);
            }
          }
        }
      },
      onError: (error) => {
        if (isTCustomAxiosError(error)) {
          if (
            error.code === 499 &&
            error.data === null &&
            error.message === '로그인 시도 횟수가 10회를 초과하였습니다.'
          ) {
            openModal({
              title: '로그인 횟수 초과',
              text: '로그인 시도 횟수가 10회 초과했습니다. 정보 보호를 위해서 비밀번호 찾기를 통해 비밀번호를 변경해 주세요.',
              onSubmit: () => navigate(ROUTE_PATH.common.FIND_PASSWORD),
              onClickOutside: () => navigate(ROUTE_PATH.common.FIND_PASSWORD),
            });
          } else if (
            error.code === 499 &&
            error.data === null &&
            error.message === '아이디 또는 비밀번호를 다시 확인해주세요.'
          ) {
            setLoginErrorMsg('아이디 또는 비밀번호를 다시 확인해 주세요.');
          } else if (error.data && error.code === 499) {
            setLoginErrorMsg(
              `아이디 또는 비밀번호를 다시 확인해 주세요. 로그인 가능 횟수(${error.data}/10)`
            );
          }
        }
      },
    }
  );

  const onValid = (data: TLoginParams) => {
    postAllUserLoginMutation({
      ...data,
      userRole: data.userRole as UserRoleType,
    });
  };

  return (
    <LoginLayout title={title}>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onValid)}>
          <div className={styles.login_wrapper}>
            <label className={styles.label}>계정 선택</label>
            <fieldset>
              <div className={styles.radio_wrapper}>
                <FormRadioBtn
                  name={LoginFormField.UserRole}
                  value={UserRoleType.STUDENT}
                  labelText="학생"
                  rules={{ required: '계정 선택은 필수입니다.' }}
                />
                <FormRadioBtn
                  name={LoginFormField.UserRole}
                  value={UserRoleType.PROFESSOR}
                  labelText="교수 (교직원)"
                  rules={{ required: '계정 선택은 필수입니다.' }}
                />
              </div>
            </fieldset>
          </div>
          <div className={styles.login_input}>
            <LabelInput
              labelText="아이디"
              placeholderText="아이디를 입력해 주세요."
              name={LoginFormField.LoginId}
              rules={{
                required: '아이디를 입력해 주세요.',
                pattern: {
                  value: /^[a-zA-Z0-9]{4,12}$/,
                  message:
                    '잘못된 입력값입니다. 입력한 정보를 다시 확인해 주세요.',
                },
              }}
            ></LabelInput>
            <LabelInput
              labelText="비밀번호"
              inputType="password"
              placeholderText="비밀번호를 입력해 주세요."
              name={LoginFormField.Password}
              rules={{
                required: '비밀번호를 입력해 주세요.',
                pattern: {
                  value: /^(.{4}|.{8,16})$/,
                  message:
                    '잘못된 입력값입니다. 입력한 정보를 다시 확인해 주세요.',
                },
              }}
            ></LabelInput>
          </div>
          <div className={styles.login_text}>
            <div className={styles.login_link}>
              <Link to={ROUTE_PATH.common.FIND_PASSWORD} className="px4">
                비밀번호 찾기
              </Link>
              <span>|</span>
              <button
                type="button"
                onClick={() =>
                  openModal({
                    title: '문의하기',
                    text: '학과사무실 (051-200-3483)로 문의해 주세요.',
                  })
                }
                className="px4"
              >
                문의하기
              </button>
            </div>
          </div>

          {loginErrorMsg === null && !firstFormErrorKey && (
            <div className={styles.div}></div>
          )}
          {!firstFormErrorKey && loginErrorMsg && (
            <span className={styles.error}>{loginErrorMsg}</span>
          )}
          {firstFormErrorKey && (
            <span className={styles.error}>
              {formErrors[firstFormErrorKey]?.message}
            </span>
          )}

          <SingleBtn
            type="submit"
            btnText="로그인"
            isBtnActive={true}
            btnStyle={{ width: '100%' }}
          />
        </form>
      </FormProvider>
    </LoginLayout>
  );
};

export default Login;
